import { z } from 'zod';
import { monetizationStatusSchema } from './channels.js';
import { periodMonthSchema } from './networks.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const REVENUE_SOURCES = ['api', 'statement', 'manual'];
export const revenueSourceSchema = z.enum(REVENUE_SOURCES);
export const REVENUE_SOURCE_LABEL = {
    api: 'YouTube API',
    statement: 'Sao kê Network',
    manual: 'Nhập tay',
};
export const REVENUE_STATUSES = ['provisional', 'final', 'reconciled'];
export const revenueStatusSchema = z.enum(REVENUE_STATUSES);
export const REVENUE_STATUS_LABEL = {
    provisional: 'Tạm tính',
    final: 'Đã chốt',
    reconciled: 'Đã đối soát',
};
/** Lệch quá ngưỡng này thì đánh dấu cảnh báo đối soát. */
export const NGUONG_LECH_PHAN_TRAM = 2;
/** Thứ tự ưu tiên mặc định khi chọn `grossUsed`. */
export const UU_TIEN_GROSS = ['statement', 'api', 'manual'];
export { periodMonthSchema };
/* ------------------------------------------------------------------ */
/* Doanh thu theo kênh                                                 */
/* ------------------------------------------------------------------ */
export const channelRevenueMonthlySchema = z.object({
    id: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    /**
     * Tình trạng kiếm tiền **đang khai** của kênh (module 01, nhập tay).
     *
     * Có ở đây để trang Doanh thu đếm "N kênh kiếm tiền" theo đúng trường đó thay
     * vì suy từ "doanh thu > 0" — kênh vừa bị tắt kiếm tiền giữa kỳ vẫn còn tiền
     * của mấy ngày đầu, suy ra sẽ đếm nhầm là đang kiếm tiền.
     *
     * **Không** dùng để lọc tiền: số tiền trong dòng này là tiền thật đã phát
     * sinh, kênh bị tắt kiếm tiền không làm nó biến mất.
     */
    monetizationStatus: monetizationStatusSchema,
    period: z.string(),
    grossApi: z.string().nullable(),
    grossStatement: z.string().nullable(),
    grossUsed: z.string(),
    grossSource: revenueSourceSchema,
    partnerId: z.string().nullable(),
    /** Nhãn hiển thị: tài khoản AdSense ghi `AdSense · <tên tài khoản>`. */
    partnerName: z.string().nullable(),
    /** `true` khi kênh nhận tiền trực tiếp qua AdSense (tỷ lệ Network luôn 0). */
    laAdsense: z.boolean(),
    sharePercent: z.string().nullable(),
    /**
     * Phần Network giữ = **(gộp − thuế Mỹ) × tỷ lệ**, không phải gộp × tỷ lệ:
     * Google khấu trừ thuế trước khi trả cho Network. Cùng một thứ tự với
     * `thucNhan` (xem `packages/shared/src/thucNhan.ts`).
     */
    partnerShare: z.string(),
    /** Gộp − thuế Mỹ, trước khi chia Network. */
    sauThue: z.string(),
    /** Công ty nhận = sau thuế − phần Network, **trước** VAT. */
    net: z.string(),
    currency: z.string(),
    status: revenueStatusSchema,
    diffPercent: z.string().nullable(),
    note: z.string().nullable(),
    /* --- Doanh thu thực nhận (cùng hàm tính với tab Hiệu suất) --- */
    /** Phần doanh thu phát sinh ở Mỹ; `null` khi chưa lấy được số theo quốc gia. */
    doanhThuMy: z.string().nullable(),
    thueMy: z.string(),
    vat: z.string(),
    /** Sau thuế Mỹ, phần Network và VAT. */
    thucNhan: z.string(),
    /** `true` khi kỳ có ngày không thuộc Network nào. */
    thieuNetwork: z.boolean(),
    /** `true` khi kỳ có ngày chưa khai thuế Mỹ — đang tính mặc định 30%. */
    thieuThueMy: z.boolean(),
    /**
     * `true` khi `sharePercent` là **bình quân** của nhiều tỷ lệ trong kỳ (kênh
     * vào / đổi Network giữa kỳ). Nhãn không được ghi "theo hợp đồng" khi cờ này
     * bật — số đó không nằm trong hợp đồng nào.
     */
    tyLeBinhQuan: z.boolean(),
});
/** Khối "Dòng tiền": gộp − Network + Content ID nhạc − phí kho nhạc = thực nhận. */
export const companyCashflowSchema = z.object({
    period: z.string(),
    currency: z.string(),
    gross: z.string(),
    /** Thuế Mỹ Google khấu trừ, trừ **trước** phần Network. */
    thueMy: z.string(),
    partnerShare: z.string(),
    /** VAT trên phần công ty nhận từ kênh. */
    vat: z.string(),
    /** Phần kênh **thực nhận**: sau thuế Mỹ, sau Network và sau VAT. */
    channelNet: z.string(),
    musicRevenue: z.string(),
    musicLicenseCost: z.string(),
    companyNet: z.string(),
    /// Quy đổi VND theo tỷ giá đã nhập, `null` khi chưa có tỷ giá cho kỳ.
    companyNetVnd: z.string().nullable(),
    exchangeRate: z.string().nullable(),
    status: revenueStatusSchema,
});
/** Kết quả `getRevenueSummary(channelIds, range)` — cũng là enricher `revenueSummary`. */
export const revenueSummaryRowSchema = z.object({
    channelId: z.string(),
    grossRevenue: z.string(),
    /** Thuế Mỹ của kỳ. */
    thueMy: z.string(),
    /** VAT của kỳ. */
    vat: z.string(),
    networkShare: z.string(),
    companyNet: z.string(),
    /**
     * RPM của kỳ, `null` khi **chưa đủ số liệu lượt xem**.
     *
     * Chỉ tính trên những ngày có **cả** doanh thu và lượt xem > 0 (xem
     * `sync.mauSoRpm`). Không trả `'0.00'` thay cho "chưa biết".
     */
    rpm: z.string().nullable(),
    /// Lượt xem đã dùng làm mẫu số RPM.
    rpmViews: z.number().int().nonnegative(),
    /// Số ngày đã vào phép tính RPM. `0` ⇒ `rpm = null`.
    rpmSoNgay: z.number().int().nonnegative(),
    currency: z.string(),
    /// Phần trăm thay đổi so với kỳ trước, `null` khi chưa đủ dữ liệu.
    deltaPercent: z.number().nullable(),
    sharePercent: z.string().nullable(),
});
/** Kỳ của enricher: `last28d`, `last7d`, `last90d` hoặc một tháng cụ thể. */
export const revenueRangeSchema = z.union([
    z.enum(['last7d', 'last28d', 'last90d']),
    periodMonthSchema,
]);
/* ------------------------------------------------------------------ */
/* Đối soát                                                            */
/* ------------------------------------------------------------------ */
export const reconciliationIssueSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    period: z.string(),
    partnerId: z.string().nullable(),
    partnerName: z.string().nullable(),
    expectedNet: z.string(),
    statementNet: z.string(),
    diffPercent: z.string(),
    currency: z.string(),
    status: revenueStatusSchema,
    note: z.string().nullable(),
});
export const resolveReconciliationInputSchema = z.object({
    action: z.enum(['confirm', 'note']),
    note: z.string().trim().max(2000).nullish(),
});
/* ------------------------------------------------------------------ */
/* Nhạc, phí và tỷ giá                                                 */
/* ------------------------------------------------------------------ */
export const musicRevenueSchema = z.object({
    id: z.string(),
    period: z.string(),
    source: z.string(),
    amount: z.string(),
    currency: z.string(),
    note: z.string().nullable(),
    file: z.string().nullable(),
});
export const musicRevenueInputSchema = z.object({
    period: periodMonthSchema,
    source: z.string().trim().min(1).max(120),
    amount: z.number(),
    currency: z.string().length(3).default('USD'),
    note: z.string().trim().max(1000).nullish(),
    file: z.string().trim().max(500).nullish(),
});
export const musicLicenseCostSchema = z.object({
    id: z.string(),
    period: z.string(),
    musicSourceId: z.string(),
    musicSourceName: z.string(),
    amount: z.string(),
    currency: z.string(),
    note: z.string().nullable(),
});
export const musicLicenseCostInputSchema = z.object({
    period: periodMonthSchema,
    musicSourceId: z.string().min(1),
    amount: z.number(),
    currency: z.string().length(3).default('USD'),
    note: z.string().trim().max(1000).nullish(),
});
export const exchangeRateSchema = z.object({
    id: z.string(),
    date: z.iso.datetime(),
    from: z.string(),
    to: z.string(),
    rate: z.string(),
});
export const exchangeRateInputSchema = z.object({
    date: z.iso.datetime(),
    from: z.string().length(3).default('USD'),
    to: z.string().length(3).default('VND'),
    rate: z.number().positive(),
});
/* ------------------------------------------------------------------ */
/* Màn hình doanh thu                                                  */
/* ------------------------------------------------------------------ */
export const revenueOverviewQuerySchema = z.object({
    period: periodMonthSchema.optional(),
    months: z.coerce.number().int().min(1).max(24).default(6),
});
/** Kênh nhận AdSense trực tiếp kèm trạng thái liên kết. */
const kenhAdsenseSchema = z.array(z.object({
    channelId: z.string(),
    channelName: z.string(),
    linkStatus: z.enum(['pending', 'linked']).nullable(),
    gross: z.string(),
}));
/** Nguồn thanh toán theo từng Network và AdSense. */
const theoDoiTacSchema = z.array(z.object({
    partnerId: z.string(),
    partnerName: z.string(),
    channelCount: z.number().int().nonnegative(),
    gross: z.string(),
    /** Thuế Mỹ, trừ **trước** phần Network. */
    thueMy: z.string(),
    partnerShare: z.string(),
    /** Công ty nhận, trước VAT. */
    net: z.string(),
    /**
     * VAT trên phần công ty nhận.
     *
     * Cộng từ cột `vat` của **chính các dòng doanh thu** thuộc đối tác này —
     * không phải một phép tính riêng, cùng cách `thueMy` và `thucNhan` đã làm.
     * Thêm 19/10/2026 vì sheet Excel "Theo Network" khai cột "VAT (USD)" mà
     * không có trường nào để ghi vào, nên cột đó trống trong mọi file đã xuất.
     */
    vat: z.string(),
    /** Sau thuế Mỹ, phần Network và VAT. */
    thucNhan: z.string(),
}));
export const revenueOverviewSchema = z.object({
    cashflow: companyCashflowSchema,
    /// Biểu đồ cột chồng các tháng gần đây.
    monthly: z.array(z.object({
        period: z.string(),
        channelNet: z.string(),
        musicNet: z.string(),
        total: z.string(),
    })),
    adsenseChannels: kenhAdsenseSchema,
    byPartner: theoDoiTacSchema,
    currency: z.string(),
});
/* ------------------------------------------------------------------ */
/* Màn hình doanh thu của người bị giới hạn theo kênh được giao         */
/* ------------------------------------------------------------------ */
/**
 * Dòng tiền **chỉ gồm các kênh được xem**.
 *
 * Khác `CompanyCashflow` ở ba chỗ, và cả ba là cố ý:
 *
 * - **không** có `musicRevenue` / `musicLicenseCost`: Content ID kho nhạc và
 *   phí kho nhạc hợp tác không gắn kênh nào — chúng là khoản thu/chi của cả
 *   công ty (xem rà soát bảo mật 16/10/2026, mục V4a);
 * - **không** có `companyNet`: cộng thêm phần nhạc vào mới ra "công ty thực
 *   nhận", mà phần nhạc thì người bị giới hạn không được xem, nên ở đây dòng
 *   cuối là `channelNet` — thực nhận **từ kênh**, không phải của công ty;
 * - **không** có `companyNetVnd` / `exchangeRate`: tỷ giá cũng là số của cả
 *   công ty (cùng lý do với bản Excel rút gọn).
 */
export const cashflowKenhSchema = z.object({
    period: z.string(),
    currency: z.string(),
    gross: z.string(),
    /** Thuế Mỹ Google khấu trừ, trừ **trước** phần Network. */
    thueMy: z.string(),
    partnerShare: z.string(),
    /** VAT trên phần công ty nhận từ kênh. */
    vat: z.string(),
    /** Thực nhận từ các kênh được xem: sau thuế Mỹ, sau Network và sau VAT. */
    channelNet: z.string(),
    status: revenueStatusSchema,
});
/**
 * Tổng quan doanh thu **theo kênh được giao** — bản của `GET /overview` dành
 * cho người bị giới hạn kênh.
 *
 * Cùng công thức (Gộp → Thuế Mỹ → Phần Network → VAT → Thực nhận) và cùng hàm
 * tính với bản toàn công ty; chỉ khác tập kênh được cộng vào.
 */
export const revenueOverviewKenhSchema = z.object({
    period: z.string(),
    currency: z.string(),
    /** Số kênh được xem đã cộng vào các số trên trang. */
    soKenh: z.number().int().nonnegative(),
    cashflow: cashflowKenhSchema,
    /// Biểu đồ cột các tháng gần đây — chỉ phần kênh, không có mảnh nhạc.
    monthly: z.array(z.object({ period: z.string(), channelNet: z.string() })),
    /// Chỉ các kênh được xem đang nhận AdSense trực tiếp.
    adsenseChannels: kenhAdsenseSchema,
    /// Đối tác của các kênh được xem; mỗi số cộng từ đúng các kênh đó.
    byPartner: theoDoiTacSchema,
});
export const exportRevenueQuerySchema = z.object({
    period: periodMonthSchema,
    /// Có thêm cột quy đổi VND hay không.
    withVnd: z.stringbool().default(true),
});
//# sourceMappingURL=revenue.js.map